/**
 * Utility functions for transforming subscription data for API payloads
 */

/**
 * Transform formik subscription details to API payload format
 * @param {Object} subscriptionDetails - The subscription details from formik
 * @param {number} addInId - The add-in ID
 * @returns {Array} Formatted subscriptions array for API payload
 */
export const formatSubscriptionsForPayload = (
  subscriptionDetails,
  addInId = 0,
) => {
  // costType: 1 = free, 2 = paid
  if (!subscriptionDetails || subscriptionDetails.costType === 1) {
    return [];
  }

  const subscriptions = [];
  const { subscriberPlans = [], enterprisePlans = [] } = subscriptionDetails;

  // Process subscriber plans (isIndividual: true)
  if (Array.isArray(subscriberPlans)) {
    subscriberPlans.forEach((plan) => {
      subscriptions.push(formatSubscriptionPlan(plan, addInId, true));
    });
  }

  // Process enterprise plans (isIndividual: false)
  if (Array.isArray(enterprisePlans)) {
    enterprisePlans.forEach((plan) => {
      subscriptions.push(formatSubscriptionPlan(plan, addInId, false));
    });
  }

  return subscriptions;
};

/**
 * Format individual subscription plan
 * @param {Object} plan - The subscription plan
 * @param {number} addInId - The add-in ID
 * @param {boolean} isIndividual - Whether this is an individual (subscriber) plan
 * @returns {Object} Formatted subscription object
 */
const formatSubscriptionPlan = (plan, addInId, isIndividual) => {
  // Determine if subscription is recurring (frequency !== 99 which is One-Time)
  const isRecurring = plan.frequency && plan.frequency !== 99;

  const baseSubscription = {
    addInSubscriptionId: plan.addInSubscriptionId || 0,
    addInId: addInId,
    name: plan.name || "",
    frequency: plan.frequency || 0,
    baseValue: Number.parseFloat(plan.baseValue) || 0,
    actualValue: Number.parseFloat(plan.actualValue || plan.baseValue) || 0,
    oneTimeCost: !isRecurring ? Number.parseFloat(plan.baseValue) || 0 : 0,
    isRecurring: plan.isRecurring || false,
    isIndividual: isIndividual,
    effectiveFromDate: plan.effectiveFromDate
      ? new Date(plan.effectiveFromDate).toISOString()
      : null,
    effectiveToDate: plan.effectiveToDate
      ? new Date(plan.effectiveToDate).toISOString()
      : null,
    enableTrial: plan.enableTrial || false,
    trialDurationDays: plan.enableTrial ? plan.trialDurationDays || 0 : 0,
    autoRenewal: plan.autoRenewal === undefined ? true : plan.autoRenewal,
    status: plan.status || 1,
    hasActiveSubscriptions: plan.hasActiveSubscriptions || false,
    addInPurchaseHistoryId: plan.addInPurchaseHistoryId || 0,
    numberOfEnrolledUser: plan.numberOfEnrolledUser || 0,
    individualEgEnrolledUser: plan.individualEgEnrolledUser || 0,
  };

  // Only add enterpriseGroupIds for enterprise plans (not individual)
  if (!isIndividual) {
    const groupIds = plan.enterpriseGroupIds || [];

    // If "All Enterprise Groups" (value 0) is selected, send only [0]
    if (groupIds.includes(0) || groupIds.includes("All Enterprise Groups")) {
      baseSubscription.enterpriseGroupIds = [0];
    } else {
      // Otherwise, send the selected group IDs
      baseSubscription.enterpriseGroupIds = groupIds.map((id) => {
        if (typeof id === "string") {
          const parsed = Number.parseInt(id, 10);
          return Number.isNaN(parsed) ? id : parsed;
        }
        return id;
      });
    }
  }

  return baseSubscription;
};

/**
 * Transform API response subscriptions back to formik format
 * @param {Array} subscriptions - The subscriptions array from API response
 * @returns {Object} Formatted subscription details for formik
 */
export const parseSubscriptionsFromResponse = (subscriptions) => {
  if (!subscriptions || subscriptions.length === 0) {
    return {
      costType: 1,
      subscriberPlans: [],
      enterprisePlans: [],
    };
  }

  const subscriberPlans = [];
  const enterprisePlans = [];

  subscriptions.forEach((subscription) => {
    const plan = {
      id: subscription.addInSubscriptionId || Date.now(),
      addInSubscriptionId: subscription.addInSubscriptionId,
      name: subscription.name || "",
      frequency: subscription.frequency || 0,
      baseValue: subscription.baseValue || "",
      actualValue: subscription.actualValue || "",
      effectiveFromDate: subscription.effectiveFromDate || null,
      effectiveToDate: subscription.effectiveToDate || null,
      autoRenewal:
        subscription.autoRenewal === undefined
          ? true
          : subscription.autoRenewal,
      enableTrial: subscription.enableTrial || false,
      trialDurationDays: subscription.trialDurationDays || 0,
      status: subscription.status || 1,
      hasActiveSubscriptions: subscription.hasActiveSubscriptions || false,
      addInPurchaseHistoryId: subscription.addInPurchaseHistoryId || 0,
      numberOfEnrolledUser: subscription.numberOfEnrolledUser || 0,
      individualEgEnrolledUser: subscription.individualEgEnrolledUser || 0,
      isRecurring: subscription.isRecurring || false,
      oneTimeCost: subscription.oneTimeCost || 0,
    };

    if (subscription.isIndividual) {
      subscriberPlans.push(plan);
    } else {
      plan.enterpriseGroupIds = subscription.enterpriseGroupIds || [];
      enterprisePlans.push(plan);
    }
  });

  // costType: 1 = free (no subscriptions), 2 = paid (has subscriptions)
  return {
    costType: subscriberPlans.length > 0 || enterprisePlans.length > 0 ? 2 : 1,
    subscriberPlans,
    enterprisePlans,
  };
};
